import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { Warehouse, WarehouseType } from 'src/app/graphql/types';
import { BaseDialogComponent } from 'src/app/main/pages/generals/dialog/base-dialog/base-dialog.component';
import { LoadingComponent } from 'src/app/main/pages/generals/loading/loading.component';
import { FormValidator } from 'src/app/shared/utils/form-validator';

import { WarehouseService } from '../../../../../services/warehouse.service';

@Component({
  selector: 'app-warehouse-dialog',
  templateUrl: './warehouse-dialog.component.html',
  styleUrls: ['./warehouse-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    BaseDialogComponent,
    LoadingComponent,
  ],
})
export class WarehouseDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<WarehouseDialogComponent>);
  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private service = inject(WarehouseService);
  private _snackBar = inject(MatSnackBar);
  private _unsubscribeAll!: Subject<any>;

  public data: { warehouseId: number; businessUnitId: number } =
    inject(MAT_DIALOG_DATA);
  isLoading = false;
  notification: any = {};
  form!: FormGroup;
  warehTypeList: WarehouseType[] | null = [];
  warehouseNumber = 0;
  txtBtn = 'AGREGAR';

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.formInit();
    this.load();
    this.loadAllWarehouseType();
    this.createCompoundNumber();
  }

  load(): void {
    if (this.data.warehouseId > 0) {
      this.service.getById(this.data.warehouseId).subscribe(data => {
        if (data) {
          this.loadInForm(data);
          this.txtBtn = 'ACTUALIZAR';
        }
        this.isLoading = false;
      });
    } else {
      this.loadWarehouseNumber();
      this.form.patchValue({
        businessUnitId: this.data.businessUnitId,
      });
    }
  }

  private loadWarehouseNumber(): void {
    this.service
      .getWarehouseNumber(this.data.businessUnitId)
      .subscribe(data => {
        if (data) {
          this.warehouseNumber = data.number as number;
        }
      });
  }

  private loadAllWarehouseType() {
    this.service.getAllWarehouseType().subscribe(data => {
      this.warehTypeList = data;
    });
  }

  private createCompoundNumber() {
    this.form.get('type')?.valueChanges.subscribe(typeId => {
      const type = this.warehTypeList?.find(typeItem => typeItem.id === typeId);
      this.setCompoundNumber(type?.shortName?.toString() ?? null);
    });
  }

  submit(): void {
    this.isLoading = true;
    let serviceAction;
    if (this.form.get('id')?.value === 0) {
      serviceAction = this.service.save(this.form);
    } else {
      serviceAction = this.service.update(this.form);
    }
    serviceAction.subscribe(id => {
      if (id) {
        this.dialogRef.close(id);
      }
      this.isLoading = false;
    });
  }

  private setCompoundNumber(shortNameType: string | null) {
    const number = this.warehouseNumber;
    const strNumber = number < 10 ? `0${number}` : number;
    const compoundNumber = `${shortNameType}-${strNumber}`;
    this.form.patchValue({ compoundNumber: compoundNumber });
  }

  private formInit(): void {
    this.form = this.fb.group(
      {
        id: [0],
        businessUnitId: [0],
        name: ['', Validators.required],
        compoundNumber: [{ value: '', disabled: true }],
        type: ['', Validators.required],
        user: [1],
        active: true,
      },
      { updateOn: 'blur' },
    );
    this.fieldNotificationInit();
  }

  private fieldNotificationInit(): void {
    const fieldsNoti = {
      name: { required: 'Nombre es requerido' },
      type: { required: 'Tipo es requerido' },
    };
    this.fv.setFormValidator(this.form, this.notification, fieldsNoti);
  }

  private loadInForm(data: Warehouse): void {
    this.warehouseNumber = data.number as number;
    this.form.patchValue({
      id: data.id,
      businessUnitId: data.businessUnitId,
      name: data.name,
      compoundNumber: '',
      type: data.type?.id,
      user: data.user?.id,
      active: data.active,
    });
    this.setCompoundNumber(data.type?.shortName?.toString() ?? null);
  }

  controlHasError(formControlName: string): boolean {
    return this.fv.fieldHasError(formControlName);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
